/**
 * Bluetooth Printer Manager
 * Menggunakan Web Bluetooth API untuk koneksi ke printer thermal (ESC/POS)
 */

class BluetoothPrinter {
  constructor() {
    this.device = null;
    this.characteristic = null;
    this.encoder = new TextEncoder();
    this._disconnectHandlers = new Set();
    this._reconnectAttempts = 3;
    this._disconnectAttached = false;
  }

  _attachDisconnectHandler() {
    if (!this.device) return;
    try {
      if (!this._disconnectAttached) {
        this.device.addEventListener(
          "gattserverdisconnected",
          this._onDisconnected.bind(this),
        );
        this._disconnectAttached = true;
      }
    } catch (e) {
      // ignore
    }
  }

  _onDisconnected(ev) {
    console.warn("Bluetooth device disconnected", ev);
    this.characteristic = null;
    this._notifyDisconnect();
    // try auto-reconnect in background
    (async () => {
      for (let i = 0; i < this._reconnectAttempts; i++) {
        try {
          const res = await this.connect({ retries: 1 });
          if (res.success) return;
        } catch (e) {
          // continue
        }
        await new Promise((r) => setTimeout(r, 1000 * (i + 1)));
      }
    })();
  }

  _notifyDisconnect() {
    for (const cb of Array.from(this._disconnectHandlers)) {
      try {
        cb(this.device);
      } catch (e) {
        /* ignore */
      }
    }
  }

  onDisconnected(cb) {
    if (typeof cb === "function") this._disconnectHandlers.add(cb);
  }

  removeDisconnected(cb) {
    this._disconnectHandlers.delete(cb);
  }

  // ESC/POS Commands
  INIT = "\x1B\x40";
  ALIGN_CENTER = "\x1B\x61\x31";
  ALIGN_LEFT = "\x1B\x61\x30";
  BOLD_ON = "\x1B\x21\x08";
  BOLD_OFF = "\x1B\x21\x00";
  LINE_FEED = "\x0A";
  CUT_PAPER = "\x1D\x56\x41\x03";

  isSupported() {
    return "bluetooth" in navigator;
  }

  async autoReconnect() {
    if (!this.isSupported()) return { success: false };
    try {
      if (!navigator.bluetooth.getDevices) return { success: false };
      const devices = await navigator.bluetooth.getDevices();
      if (devices.length > 0) {
        this.device = devices[0];
        if (this.device?.gatt) {
          try {
            const server = await this.device.gatt.connect();
            const service = await server.getPrimaryService(
              "000018f0-0000-1000-8000-00805f9b34fb",
            );
            this.characteristic = await service.getCharacteristic(
              "00002af1-0000-1000-8000-00805f9b34fb",
            );
            this._attachDisconnectHandler();
            return { success: true, deviceName: this.device.name };
          } catch (err) {
            return { success: false, error: err.message };
          }
        }
      }
      return { success: false };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async connect({ retries = this._reconnectAttempts } = {}) {
    if (!this.isSupported()) {
      return {
        success: false,
        error: "Web Bluetooth tidak didukung di browser ini",
      };
    }
    try {
      if (this.device?.gatt?.connected) {
        return { success: true, deviceName: this.device.name };
      }
      if (this.device?.gatt && !this.device.gatt.connected) {
        let lastErr = null;
        for (let i = 0; i <= retries; i++) {
          try {
            const server = await this.device.gatt.connect();
            const service = await server.getPrimaryService(
              "000018f0-0000-1000-8000-00805f9b34fb",
            );
            this.characteristic = await service.getCharacteristic(
              "00002af1-0000-1000-8000-00805f9b34fb",
            );
            this._attachDisconnectHandler();
            return { success: true, deviceName: this.device.name };
          } catch (err) {
            lastErr = err;
            await new Promise((r) => setTimeout(r, 300 * Math.pow(2, i)));
          }
        }
        return { success: false, error: lastErr?.message || "Reconnect gagal" };
      }
      this.device = await navigator.bluetooth.requestDevice({
        filters: [{ services: ["000018f0-0000-1000-8000-00805f9b34fb"] }],
        optionalServices: ["000018f0-0000-1000-8000-00805f9b34fb"],
      });
      const server = await this.device.gatt.connect();
      const service = await server.getPrimaryService(
        "000018f0-0000-1000-8000-00805f9b34fb",
      );
      this.characteristic = await service.getCharacteristic(
        "00002af1-0000-1000-8000-00805f9b34fb",
      );
      this._attachDisconnectHandler();
      return { success: true, deviceName: this.device.name };
    } catch (error) {
      return { success: false, error: error.message || String(error) };
    }
  }

  async disconnect() {
    try {
      if (this.device?.gatt?.connected) {
        await this.device.gatt.disconnect();
      }
    } finally {
      this.characteristic = null;
      this._notifyDisconnect();
    }
  }

  isConnected() {
    return !!(this.device?.gatt?.connected && this.characteristic);
  }

  async sendData(text) {
    if (!this.characteristic) throw new Error("Printer tidak terhubung");
    const data = this.encoder.encode(text);
    const chunkSize = 512;
    for (let i = 0; i < data.length; i += chunkSize) {
      await this.characteristic.writeValue(data.slice(i, i + chunkSize));
      await new Promise((r) => setTimeout(r, 50));
    }
  }

  // Helper: pad left-right columns to 32 chars
  _cols(left, right) {
    const padding = Math.max(0, 32 - left.length - right.length);
    return left + " ".repeat(padding) + right;
  }

  /**
   * Print receipt for carwash transaction
   * @param {Object} txData - transaction data
   *   - invoice_number, created_at, cashier_name, customer_name
   *   - plate_number, vehicle_type
   *   - details: [{ item_type, item_name, unit_price, quantity, subtotal }]
   *   - total_amount, discount_amount, paid_amount, payment_method
   * @param {Object} settings - store settings (store_name, store_address, store_phone, receipt_footer)
   */
  async printReceipt(txData, settings = {}) {
    if (!this.isConnected()) throw new Error("Printer tidak terhubung");
    try {
      const storeName = settings.store_name || "Car Wash";
      const storeAddress = settings.store_address || "";
      const storePhone = settings.store_phone || "";
      const footer = settings.receipt_footer || "Terima kasih!";

      const fmtNum = (n) =>
        Number(n || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });
      const fmtDate = (d) => {
        const dt = new Date(d);
        return (
          dt.toLocaleDateString("id-ID", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }) +
          ", " +
          dt.toLocaleTimeString("id-ID", {
            hour: "2-digit",
            minute: "2-digit",
            hourCycle: "h23",
          })
        );
      };

      let r = this.INIT;

      // === HEADER ===
      r += this.ALIGN_CENTER;
      r += this.BOLD_ON + storeName + this.LINE_FEED + this.BOLD_OFF;
      if (storeAddress) r += storeAddress + this.LINE_FEED;
      if (storePhone) r += "Telp: " + storePhone + this.LINE_FEED;
      r += "================================" + this.LINE_FEED;

      // === TRANSACTION INFO ===
      r += this.ALIGN_LEFT;
      r += "No: " + (txData.invoice_number || txData.id) + this.LINE_FEED;
      r += "Tgl: " + fmtDate(txData.created_at) + this.LINE_FEED;
      if (txData.cashier_name)
        r += "Kasir: " + txData.cashier_name + this.LINE_FEED;
      if (txData.customer_name)
        r += "Customer: " + txData.customer_name + this.LINE_FEED;
      if (txData.plate_number || txData.vehicle_type) {
        const vehicleInfo = [txData.plate_number, txData.vehicle_type]
          .filter(Boolean)
          .join(" - ");
        r += "Kendaraan: " + vehicleInfo + this.LINE_FEED;
      }
      r += "--------------------------------" + this.LINE_FEED;

      // === ITEMS ===
      const details = txData.details || [];
      const services = details.filter((d) => d.item_type === "service");
      const products = details.filter((d) => d.item_type === "product");

      if (services.length > 0) {
        r += "[LAYANAN]" + this.LINE_FEED;
        services.forEach((item) => {
          let name = item.item_name || "";
          if (name.length > 20) name = name.substring(0, 20);
          r += name + this.LINE_FEED;

          const isLoyaltyFree = item.notes === "GRATIS - Redeem Loyalty";
          const isLoyaltyUpgrade =
            item.notes && item.notes.startsWith("Upgrade Loyalty");

          if (isLoyaltyFree || isLoyaltyUpgrade) {
            const origLine = `  ${item.quantity} x ${fmtNum(item.unit_price)}`;
            r += this._cols(origLine, fmtNum(item.unit_price * item.quantity)) + this.LINE_FEED;
            r +=
              this._cols(
                "  Diskon Loyalty",
                "- " + fmtNum(item.discount),
              ) + this.LINE_FEED;
            r +=
              this._cols(
                "  Subtotal",
                isLoyaltyFree ? "GRATIS" : fmtNum(item.subtotal),
              ) + this.LINE_FEED;
          } else {
            const priceLine = `${item.quantity} x ${fmtNum(item.unit_price)}`;
            r +=
              this._cols("  " + priceLine, fmtNum(item.subtotal)) +
              this.LINE_FEED;
          }
        });
      }

      if (products.length > 0) {
        r += "[PRODUK]" + this.LINE_FEED;
        products.forEach((item) => {
          let name = item.item_name || "";
          if (name.length > 20) name = name.substring(0, 20);
          r += name + this.LINE_FEED;
          const priceLine = `${item.quantity} x ${fmtNum(item.unit_price)}`;
          r +=
            this._cols("  " + priceLine, fmtNum(item.subtotal)) +
            this.LINE_FEED;
        });
      }

      r += "--------------------------------" + this.LINE_FEED;

      // === TOTALS ===
      const discountAmount = Number(txData.discount_amount || 0);
      if (discountAmount > 0) {
        const subtotal = details.reduce(
          (s, d) => s + Number(d.subtotal || 0),
          0,
        );
        r += this._cols("Subtotal", fmtNum(subtotal)) + this.LINE_FEED;
        r +=
          this._cols("Diskon", "- " + fmtNum(discountAmount)) + this.LINE_FEED;
        r += "--------------------------------" + this.LINE_FEED;
      }

      r += this.BOLD_ON;
      r += this._cols("TOTAL", fmtNum(txData.total_amount)) + this.LINE_FEED;
      r += this.BOLD_OFF;

      // === PAYMENT ===
      const method = (txData.payment_method || "").toUpperCase();
      r += this._cols("Pembayaran", method) + this.LINE_FEED;
      if (txData.payment_method === "cash") {
        const paid = Number(txData.paid_amount || txData.total_amount);
        const change = paid - Number(txData.total_amount);
        r += this._cols("Dibayar", fmtNum(paid)) + this.LINE_FEED;
        r +=
          this._cols("Kembali", fmtNum(Math.max(0, change))) + this.LINE_FEED;
      }

      // === FOOTER ===
      r += "--------------------------------" + this.LINE_FEED;
      r += this.ALIGN_CENTER;
      r += footer + this.LINE_FEED;
      r += this.LINE_FEED + this.LINE_FEED;
      r += this.CUT_PAPER;

      await this.sendData(r);
      return { success: true };
    } catch (error) {
      console.error("Print error:", error);
      return { success: false, error: error.message };
    }
  }
}

const bluetoothPrinter = new BluetoothPrinter();
export default bluetoothPrinter;
